/**
 * SOUNDHOUS_PHASE_6E_3 — Shopify Referral / Partner Code Component (Live Deployment)
 * ============================================================================
 * Native Shopify AJAX cart integration. NO Storefront API cart GID required.
 *
 * Flow:
 *   1. Customer enters referral/partner code.
 *   2. Component calls the canonical Soundhous backend (applyStorefrontReferralCode)
 *      which validates the referral, verifies first-purchase eligibility, resolves
 *      the governed benefit (Best Eligible Price), and creates a short-lived internal
 *      Shopify discount code (SHREF-XXXXXXXX) via the Admin API.
 *   3. Component applies SHREF via POST /cart/update.js { discount: "SHREF-..." }.
 *   4. Component re-reads GET /cart.js and verifies the real cart total equals the
 *      governed final price (tolerance max(₦1/$1, 0.5%)). On mismatch, SHREF is
 *      removed and a safe review state is shown — checkout is never blocked.
 *
 * SECURITY:
 *   - checkout_discount_code (SHREF) is a technical field — NEVER displayed in UX.
 *   - It is short-lived, single-use, transaction-specific; NOT the affiliate identity.
 *   - On remove, the backend resolves the bridge from trusted server state; the
 *     browser never supplies an affiliate_attribution_id. Attribution evidence is
 *     retained, never cancelled by a browser action.
 *
 * MOUNT: <div class="sho-referral-root"></div>  (place one in the cart drawer and
 *        one on the full cart page; the component renders into all mount points).
 *        Line items should expose a `soundhous_sku` line-item property; the
 *        component falls back to item.sku if absent.
 * ENDPOINT: https://soundhous-experience.base44.app/functions/applyStorefrontReferralCode
 */
/* global SHOReferral */
(function () {
  const ENDPOINT = "https://soundhous-experience.base44.app/functions/applyStorefrontReferralCode";
  const MOUNT_SELECTOR = ".sho-referral-root, #sho-referral-root";

  const COPY = {
    success: "✓ Referral code applied.",
    savingLabel: "Referral saving",
    priceLabel: "Your price",
    promoRecognised: "✓ Referral recognised.",
    promoNote: "Your current Soundhous promotional price already gives you the better offer.",
    invalid: "We couldn't apply that referral code. Please check the code or continue with the current Soundhous price.",
    review: "We couldn't verify this referral yet. You can continue with the current Soundhous price, or check the referral details and try again.",
    collapsedPrompt: "Have a referral or partner code?",
    collapsedCta: "Add code",
    title: "Referral / Partner Code",
    placeholder: "Enter code",
    apply: "Apply",
    checking: "Checking…",
    remove: "Remove",
    dismiss: "Dismiss",
    identityRecognised: "✓ Referral code recognised.",
    identityPrompt: "Enter your email to confirm your first-purchase referral benefit.",
    verify: "Verify referral",
    notEligible: "Referral code recognised. This benefit is available on a first qualifying purchase. You can continue with the current Soundhous price.",
  };

  let state = {
    expanded: false,
    loading: false,
    applied: null,        // last successful apply response
    error: null,
    errorCode: null,      // "invalid" | "review" | null
    codeInput: "",
    shrefCode: null,      // internal SHREF, never displayed
    preservedDiscount: null, // pre-existing discount code captured before apply
    identityRequired: false,  // backend asked for email to confirm first-purchase
    emailInput: "",
    emailError: null,
    notEligible: null,        // first-purchase not-eligible customer-safe message
  };

  let revalidateTimer = null;
  // Internal-mutation guard: when true, the fetch interceptor skips revalidation
  // for the referral component's OWN /cart/update.js (apply/remove/restore SHREF).
  let internalReferralCartMutation = false;

  // ── Self-contained premium styling (Soundhous palette). Injected once. ──
  function injectStyles() {
    if (document.getElementById("sho-referral-styles")) return;
    const css = `
.sho-referral{font-family:'Plus Jakarta Sans',system-ui,-apple-system,Segoe UI,Roboto,sans-serif;color:#2C2C24;box-sizing:border-box;-webkit-font-smoothing:antialiased}
.sho-referral *,.sho-referral *::before,.sho-referral *::after{box-sizing:border-box}
.sho-referral--collapsed{width:100%}
.sho-referral__toggle{width:100%;display:flex;justify-content:space-between;align-items:center;gap:8px;padding:12px 14px;background:#FAFAF7;border:1px solid #C9C0B0;border-radius:10px;color:#2C2C24;font-size:13px;font-weight:500;cursor:pointer;transition:border-color .15s,background .15s}
.sho-referral__toggle:hover{border-color:#A87E5E;background:#fff}
.sho-referral__add{color:#A87E5E;font-weight:600;white-space:nowrap}
.sho-referral--expanded,.sho-referral--applied{padding:14px;background:#FAFAF7;border:1px solid #C9C0B0;border-radius:12px}
.sho-referral__head{display:flex;justify-content:space-between;align-items:center;margin-bottom:10px;gap:8px}
.sho-referral__title{font-weight:600;font-size:13px;color:#2C2C24;letter-spacing:.01em}
.sho-referral__close{background:none;border:none;font-size:20px;line-height:1;color:#6B6B66;cursor:pointer;padding:0 4px;border-radius:6px}
.sho-referral__close:hover{color:#2C2C24}
.sho-referral__input-row{display:flex;gap:8px}
.sho-referral__label{position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);border:0}
.sho-referral__input{flex:1;min-width:0;padding:10px 12px;border:1px solid #C9C0B0;border-radius:8px;font-size:14px;background:#fff;color:#2C2C24;transition:border-color .15s,box-shadow .15s}
.sho-referral__input:focus{outline:none;border-color:#A87E5E;box-shadow:0 0 0 3px rgba(168,126,94,.18)}
.sho-referral__apply{padding:10px 18px;background:#2C2C24;color:#F7F5F0;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;transition:opacity .15s;white-space:nowrap}
.sho-referral__apply:disabled{opacity:.55;cursor:default}
.sho-referral__apply:not(:disabled):hover{opacity:.9}
.sho-referral__status{margin-top:10px;font-size:13px;line-height:1.45}
.sho-referral__status--ok{color:#2C2C24}
.sho-referral__status--invalid{color:#8B3A2B}
.sho-referral__status--review{color:#6B6B66}
.sho-referral__note{margin-top:6px;font-size:13px;line-height:1.45;color:#6B6B66}
.sho-referral__price{margin-top:10px;padding-top:10px;border-top:1px solid #E8E4DC}
.sho-referral__row{display:flex;justify-content:space-between;align-items:baseline;gap:12px;font-size:13px;color:#6B6B66;padding:3px 0}
.sho-referral__row--final{font-weight:600;color:#2C2C24;font-size:15px;padding-top:6px}
.sho-referral__remove{background:none;border:none;color:#A87E5E;font-size:13px;font-weight:600;cursor:pointer;padding:2px 4px;border-radius:6px}
.sho-referral__remove:hover{color:#8a6543;text-decoration:underline}
@media (max-width:480px){.sho-referral__input-row{flex-direction:column}.sho-referral__apply{width:100%}}
`;
    const el = document.createElement("style");
    el.id = "sho-referral-styles";
    el.textContent = css;
    document.head.appendChild(el);
  }

  function formatPrice(amount, currency) {
    if (amount == null || isNaN(Number(amount))) return "";
    const symbol = currency === "USD" ? "$" : "₦";
    return symbol + Number(amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  }

  function readCartLines(callback) {
    fetch("/cart.js", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((cart) => {
        const lines = (cart.items || [])
          .map((item) => ({ soundhous_sku: item.properties?.["soundhous_sku"] || item.sku || "", quantity: item.quantity || 1 }))
          .filter((l) => l.soundhous_sku);
        callback(lines, cart);
      })
      .catch(() => callback([]));
  }

  function callBackend(payload, callback) {
    state.loading = true; render();
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => r.json())
      .then((data) => { state.loading = false; callback(data); })
      .catch(() => {
        state.loading = false;
        state.error = COPY.review;
        state.errorCode = "review";
        state.applied = null; state.shrefCode = null;
        render();
      });
  }

  function applyDiscountToCart(discountValue, callback) {
    // Mark this /cart/update.js as an INTERNAL referral mutation so the global
    // fetch interceptor does NOT schedule a recursive revalidation.
    internalReferralCartMutation = true;
    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ discount: discountValue || "" }),
    })
      .then((r) => r.json())
      .then((cart) => callback(cart))
      .catch(() => callback(null))
      .finally(() => { internalReferralCartMutation = false; });
  }

  function verifyCartTotal(expectedFinalPrice, callback) {
    fetch("/cart.js", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((cart) => {
        const actualTotalCents = cart.total_price; // Shopify returns cents
        const expectedCents = Math.round(Number(expectedFinalPrice) * 100);
        const toleranceCents = Math.max(100, Math.round(expectedCents * 0.005));
        const aligned = Math.abs(actualTotalCents - expectedCents) <= toleranceCents;
        const appliedCode = cart.discount_code?.code || (cart.discount_codes && cart.discount_codes[0]?.code) || null;
        callback({ aligned, actualTotalCents, expectedCents, appliedCode, cart });
      })
      .catch(() => callback({ aligned: false, appliedCode: null }));
  }

  function scheduleRevalidate() {
    if (revalidateTimer) clearTimeout(revalidateTimer);
    revalidateTimer = setTimeout(() => {
      // Shopify replaces cart-drawer HTML on /cart/add.js etc., creating fresh
      // .sho-referral-root elements. Always re-render so the control reappears,
      // then revalidate only if a referral is already applied.
      render();
      if (state.applied) SHOReferral.revalidate();
    }, 400);
  }

  // ── Cart-change revalidation: wrap fetch once to catch /cart/*.js mutations ──
  let fetchWrapped = false;
  function wrapFetchForCartChanges() {
    if (fetchWrapped || typeof window.fetch !== "function") return;
    fetchWrapped = true;
    const orig = window.fetch;
    window.fetch = function (input, init) {
      const p = orig.apply(this, arguments);
      try {
        const url = typeof input === "string" ? input : (input && input.url) || "";
        if (/\/cart\/(add|change|update|clear)\.js(\?|$)/.test(url)) {
          // Skip revalidation for the referral component's OWN cart mutations
          // (apply/remove/restore SHREF) to prevent a recursive revalidation loop.
          p.then(() => { if (!internalReferralCartMutation) scheduleRevalidate(); }).catch(() => {});
        }
      } catch (e) { /* never break the merchant's cart */ }
      return p;
    };
  }

  window.SHOReferral = {
    toggle() { state.expanded = !state.expanded; state.error = null; state.errorCode = null; render(); if (state.expanded) focusInput(); },
    setCodeInput(v) { state.codeInput = v; },
    onInputKey(e) { if (e && e.key === "Enter") { e.preventDefault(); SHOReferral.apply(); } },
    dismiss() { state.applied = null; state.error = null; state.errorCode = null; state.shrefCode = null; state.preservedDiscount = null; state.expanded = false; state.identityRequired = false; state.emailInput = ""; state.emailError = null; state.notEligible = null; render(); },
    dismissIdentity() { state.identityRequired = false; state.emailInput = ""; state.emailError = null; state.expanded = false; render(); },
    setEmailInput(v) { state.emailInput = v; state.emailError = null; },
    onEmailKey(e) { if (e && e.key === "Enter") { e.preventDefault(); SHOReferral.verifyIdentity(); } },
    verifyIdentity() {
      const email = (state.emailInput || "").trim();
      if (!email) { state.emailError = "Please enter your email address."; render(); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { state.emailError = "Please enter a valid email address."; render(); return; }
      readCartLines((lines) => {
        callBackend({ referral_code: state.codeInput, action: "verify_identity", email, lines, buyer_track: "Unclear" }, (data) => {
          state.identityRequired = false; state.emailError = null;
          handleApplyResponse(data, state.codeInput);
        });
      });
    },
    apply() {
      const code = (state.codeInput || "").trim();
      if (!code || state.loading) return;
      readCartLines((lines, cart) => {
        const existingCode = cart?.discount_code?.code || null;
        if (existingCode && existingCode !== state.shrefCode) state.preservedDiscount = existingCode;
        callBackend({ referral_code: code, action: "apply", lines, buyer_track: "Unclear" }, (data) => {
          handleApplyResponse(data, code);
        });
      });
    },
    remove() {
      const shref = state.shrefCode;
      const referralCode = state.applied?.checkout_attribution?.referral_code;
      callBackend({ action: "remove", checkout_discount_code: shref, referral_code: referralCode }, () => {
        // Remove SHREF from the cart, restoring preserved discount where present
        const restore = state.preservedDiscount || "";
        applyDiscountToCart(restore, () => {
          state.applied = null; state.error = null; state.errorCode = null; state.codeInput = "";
          state.shrefCode = null; state.preservedDiscount = null;
          render();
        });
      });
    },
    revalidate() {
      if (!state.applied) return;
      const code = state.applied.checkout_attribution?.referral_code;
      if (!code) return;
      readCartLines((lines) => {
        callBackend({ referral_code: code, action: "revalidate", lines, buyer_track: "Unclear" }, (data) => {
          handleApplyResponse(data, code);
        });
      });
    },
  };

  function handleApplyResponse(data, enteredCode) {
    // Identity required — referral valid, ask for email to confirm first-purchase
    if (data.status === "identity_required") {
      state.identityRequired = true; state.error = null; state.errorCode = null;
      state.applied = null; state.shrefCode = null; state.emailInput = ""; state.emailError = null;
      render(); return;
    }

    // First-purchase not eligible — customer-safe specific message
    if (data.status === "first_purchase_not_eligible") {
      state.notEligible = data.message || COPY.notEligible;
      state.applied = null; state.shrefCode = null; state.identityRequired = false; state.emailError = null;
      render(); return;
    }

    // Invalid — no SHREF
    if (data.status === "invalid" || data.status === "no_benefit_rule") {
      state.error = COPY.invalid;
      state.errorCode = "invalid";
      state.applied = null; state.shrefCode = null;
      render(); return;
    }

    // Review — no SHREF
    if (data.status === "review_required") {
      state.error = COPY.review;
      state.errorCode = "review";
      state.applied = null; state.shrefCode = null;
      render(); return;
    }

    // Promo better — no SHREF, informational card
    if (data.status === "promo_better") {
      state.applied = data; state.error = null; state.errorCode = null; state.shrefCode = null;
      render(); return;
    }

    // Benefit applied with zero saving — no SHREF to apply
    if (data.status === "benefit_applied" && !data.checkout_discount_code) {
      state.applied = data; state.error = null; state.errorCode = null; state.shrefCode = null;
      render(); return;
    }

    // Success with a SHREF code — apply to the AJAX cart and verify the governed total
    if (data.checkout_discount_code) {
      state.shrefCode = data.checkout_discount_code;
      // Non-stacking default: apply only the governed SHREF (replace existing promos).
      applyDiscountToCart(data.checkout_discount_code, () => {
        verifyCartTotal(data.final_price, (verify) => {
          if (verify.aligned && verify.appliedCode === data.checkout_discount_code) {
            state.applied = data; state.error = null; state.errorCode = null; render();
          } else {
            // SOUNDHOUS_PHASE_6E_5: Cart total does not match governed price.
            // 1. Call backend remove to retire the bridge (mark Removed) so the
            //    unusable SHREF can never be reused.
            // 2. Restore any preserved Shopify promo.
            // 3. Show safe review state.
            const mismatchShref = state.shrefCode;
            const mismatchReferral = data.checkout_attribution?.referral_code;
            callBackend({ action: "remove", checkout_discount_code: mismatchShref, referral_code: mismatchReferral }, () => {
              applyDiscountToCart(state.preservedDiscount || "", () => {
                state.applied = null; state.shrefCode = null;
                state.error = COPY.review; state.errorCode = "review";
                render();
              });
            });
          }
        });
      });
      return;
    }

    state.applied = data; state.error = null; state.errorCode = null; render();
  }

  function focusInput() {
    setTimeout(() => {
      const el = document.querySelector(".sho-referral__input");
      if (el && typeof el.focus === "function") el.focus();
    }, 30);
  }

  function buildHtml() {
    // ── Identity required: referral valid, ask for email ──
    if (state.identityRequired) {
      return `
        <div class="sho-referral sho-referral--expanded" id="sho-referral-panel">
          <div class="sho-referral__head">
            <span class="sho-referral__title">${escapeHtml(COPY.title)}</span>
            <button type="button" class="sho-referral__close" onclick="SHOReferral.dismissIdentity()" aria-label="Close">×</button>
          </div>
          <div class="sho-referral__status sho-referral__status--ok">${escapeHtml(COPY.identityRecognised)}</div>
          <div class="sho-referral__note">${escapeHtml(COPY.identityPrompt)}</div>
          <div class="sho-referral__input-row">
            <label class="sho-referral__label" for="sho-referral-email">Email address</label>
            <input id="sho-referral-email" type="email" class="sho-referral__input" placeholder="Email address" value="${escapeHtml(state.emailInput)}" oninput="SHOReferral.setEmailInput(this.value)" onkeydown="SHOReferral.onEmailKey(event)" aria-describedby="sho-referral-email-status" autocomplete="email" />
            <button type="button" class="sho-referral__apply" onclick="SHOReferral.verifyIdentity()" ${state.loading ? "disabled" : ""}>${state.loading ? escapeHtml(COPY.checking) : escapeHtml(COPY.verify)}</button>
          </div>
          ${state.emailError ? `<div id="sho-referral-email-status" class="sho-referral__status sho-referral__status--invalid" role="status" aria-live="polite">${escapeHtml(state.emailError)}</div>` : ""}
        </div>`;
    }

    // ── First-purchase not eligible — customer-safe message ──
    if (state.notEligible) {
      return `
        <div class="sho-referral sho-referral--applied" role="status" aria-live="polite">
          <div class="sho-referral__head">
            <span class="sho-referral__title">${escapeHtml(COPY.title)}</span>
            <button type="button" class="sho-referral__remove" onclick="SHOReferral.dismiss()" aria-label="${escapeHtml(COPY.dismiss)}">${escapeHtml(COPY.dismiss)}</button>
          </div>
          <div class="sho-referral__status sho-referral__status--review">${escapeHtml(state.notEligible)}</div>
        </div>`;
    }

    // ── Applied: referral benefit with SHREF ──
    if (state.applied && (state.applied.status === "success" || state.applied.status === "benefit_applied") && state.applied.checkout_discount_code) {
      const a = state.applied;
      const saving = (Number(a.referral_saving) > 0) ? Number(a.referral_saving) : null;
      const priceRow = saving !== null
        ? `<div class="sho-referral__row"><span>${escapeHtml(COPY.savingLabel)}</span><span>${formatPrice(saving, a.currency)}</span></div>
           <div class="sho-referral__row sho-referral__row--final"><span>${escapeHtml(COPY.priceLabel)}</span><span>${formatPrice(a.final_price, a.currency)}</span></div>`
        : `<div class="sho-referral__row sho-referral__row--final"><span>${escapeHtml(COPY.priceLabel)}</span><span>${formatPrice(a.final_price, a.currency)}</span></div>`;
      return `
        <div class="sho-referral sho-referral--applied" role="status" aria-live="polite">
          <div class="sho-referral__head">
            <span class="sho-referral__title">${escapeHtml(COPY.title)}</span>
            <button type="button" class="sho-referral__remove" onclick="SHOReferral.remove()" aria-label="${escapeHtml(COPY.remove)}">${escapeHtml(COPY.remove)}</button>
          </div>
          <div class="sho-referral__status sho-referral__status--ok">${escapeHtml(COPY.success)}</div>
          <div class="sho-referral__price">${priceRow}</div>
        </div>`;
    }

    // ── Applied: promo better (no SHREF) ──
    if (state.applied && state.applied.status === "promo_better") {
      return `
        <div class="sho-referral sho-referral--applied" role="status" aria-live="polite">
          <div class="sho-referral__head">
            <span class="sho-referral__title">${escapeHtml(COPY.title)}</span>
            <button type="button" class="sho-referral__remove" onclick="SHOReferral.dismiss()" aria-label="${escapeHtml(COPY.dismiss)}">${escapeHtml(COPY.dismiss)}</button>
          </div>
          <div class="sho-referral__status sho-referral__status--ok">${escapeHtml(COPY.promoRecognised)}</div>
          <div class="sho-referral__note">${escapeHtml(COPY.promoNote)}</div>
        </div>`;
    }

    // ── Applied: benefit applied, zero saving, no SHREF ──
    if (state.applied && state.applied.status === "benefit_applied" && !state.applied.checkout_discount_code) {
      const a = state.applied;
      return `
        <div class="sho-referral sho-referral--applied" role="status" aria-live="polite">
          <div class="sho-referral__head">
            <span class="sho-referral__title">${escapeHtml(COPY.title)}</span>
            <button type="button" class="sho-referral__remove" onclick="SHOReferral.dismiss()" aria-label="${escapeHtml(COPY.dismiss)}">${escapeHtml(COPY.dismiss)}</button>
          </div>
          <div class="sho-referral__status sho-referral__status--ok">${escapeHtml(COPY.success)}</div>
          <div class="sho-referral__price"><div class="sho-referral__row sho-referral__row--final"><span>${escapeHtml(COPY.priceLabel)}</span><span>${formatPrice(a.final_price, a.currency)}</span></div></div>
        </div>`;
    }

    // ── Collapsed ──
    if (!state.expanded) {
      return `
        <div class="sho-referral sho-referral--collapsed">
          <button type="button" class="sho-referral__toggle" onclick="SHOReferral.toggle()" aria-expanded="false" aria-controls="sho-referral-panel">
            <span>${escapeHtml(COPY.collapsedPrompt)}</span>
            <span class="sho-referral__add">${escapeHtml(COPY.collapsedCta)}</span>
          </button>
        </div>`;
    }

    // ── Expanded input ──
    const errClass = state.errorCode === "invalid" ? "sho-referral__status--invalid" : "sho-referral__status--review";
    return `
      <div class="sho-referral sho-referral--expanded" id="sho-referral-panel">
        <div class="sho-referral__head">
          <span class="sho-referral__title">${escapeHtml(COPY.title)}</span>
          <button type="button" class="sho-referral__close" onclick="SHOReferral.toggle()" aria-label="Close">×</button>
        </div>
        <div class="sho-referral__input-row">
          <label class="sho-referral__label" for="sho-referral-code">${escapeHtml(COPY.title)}</label>
          <input id="sho-referral-code" type="text" class="sho-referral__input" placeholder="${escapeHtml(COPY.placeholder)}" value="${escapeHtml(state.codeInput)}" oninput="SHOReferral.setCodeInput(this.value)" onkeydown="SHOReferral.onInputKey(event)" aria-describedby="sho-referral-status" autocomplete="off" />
          <button type="button" class="sho-referral__apply" onclick="SHOReferral.apply()" ${state.loading ? "disabled" : ""} aria-label="${escapeHtml(COPY.apply)}">${state.loading ? escapeHtml(COPY.checking) : escapeHtml(COPY.apply)}</button>
        </div>
        ${state.error ? `<div id="sho-referral-status" class="sho-referral__status ${errClass}" role="status" aria-live="polite">${escapeHtml(state.error)}</div>` : ""}
      </div>`;
  }

  function render() {
    const roots = document.querySelectorAll(MOUNT_SELECTOR);
    if (!roots.length) return;
    const html = buildHtml();
    roots.forEach((r) => { r.innerHTML = html; });
  }

  // ── Boot ──
  injectStyles();
  wrapFetchForCartChanges();

  document.addEventListener("DOMContentLoaded", () => {
    render();
    // Shopify theme cart events (cross-theme best-effort; fetch wrap is the primary backstop)
    document.addEventListener("cart:updated", scheduleRevalidate);
    document.addEventListener("cart:refresh", scheduleRevalidate);
    document.addEventListener("cart:change", scheduleRevalidate);
    document.addEventListener("cart:added", scheduleRevalidate);
    document.addEventListener("cart:removed", scheduleRevalidate);
  });

  // Render immediately if DOM is already ready (late-loaded scripts)
  if (document.readyState !== "loading") render();
})();